import { useToast as useToastDuPackage } from '@pms-digital/design-system';
import { useMemo } from 'react';

/** A neutral toast closes by itself after this delay (the minimum the package accepts). */
export const DUREE_TOAST_NEUTRE_MS = 3000;

export type ToastVariant = 'erreur' | 'neutre';

/** The one button a toast can carry next to its message ("voir le résultat", "relancer"). */
export interface ToastAction {
  libelle: string;
  onClick: () => void;
}

export interface ToastOptions {
  variant: ToastVariant;
  message: string;
  /**
   * What a screen reader announces instead of the message, when it has more to say than the eye
   * needs. The message is then hidden from the assistive technologies, so it is not read twice.
   */
  annonce?: string;
  /** A button next to the message; the toast closes once it is clicked. */
  action?: ToastAction;
  /** Delay before it closes by itself; none for an error, 3 s for a neutral one. */
  dureeMs?: number;
}

interface ToastApi {
  afficher: (options: ToastOptions) => void;
}

/**
 * The toasts of the PMS design system, with the words Rosetta's screens already speak
 * (`variant`, `annonce`, `action.libelle`): a mapping of props, no styling. The package draws the
 * toast, holds it while the pointer or the focus is on it, closes it after its delay and gives the
 * focus back when it closes. `annonce` is the package's `announce`: the sentence a screen reader
 * reads instead of the visible message.
 */
export function useToast(): ToastApi {
  const afficherToast = useToastDuPackage();
  return useMemo<ToastApi>(
    () => ({
      afficher: ({ variant, message, annonce, action, dureeMs }) => {
        const erreur = variant === 'erreur';
        afficherToast(message, {
          ...(annonce === undefined ? {} : { announce: annonce }),
          tone: erreur ? 'error' : 'neutral',
          // An error stays until the user closes it, unless the caller gives it a delay.
          persistent: erreur && dureeMs === undefined,
          duration: dureeMs ?? DUREE_TOAST_NEUTRE_MS,
          ...(action === undefined
            ? {}
            : { action: { label: action.libelle, onClick: action.onClick } }),
        });
      },
    }),
    [afficherToast],
  );
}
